/**
 * ProcessingStep.js
 *
 * Step 3: AI extraction in progress.
 */

import React from 'react';
import { Loader2, FileText, AlertCircle, RefreshCw, CheckCircle, Sparkles } from 'lucide-react';

const ProcessingStep = ({ file, isProcessing, error, onRetry }) => {
  // Processing stages for visual feedback
  const stages = [
    { id: 'upload', label: 'Preparing document', complete: true },
    { id: 'analyze', label: 'AI analyzing content', complete: !isProcessing && !error },
    { id: 'extract', label: 'Extracting items', complete: !isProcessing && !error },
    { id: 'validate', label: 'Validating results', complete: !isProcessing && !error }
  ];

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="h-8 w-8 text-red-500" />
          </div>
          <h3 className="text-lg font-semibold text-slate-800">Extraction Failed</h3>
          <p className="text-sm text-slate-500 mt-1">
            We couldn't extract items from this document
          </p>
        </div>

        <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-6">
          <p className="text-sm text-red-700">{error}</p>
        </div>

        <div className="flex flex-col items-center gap-4">
          <button
            onClick={onRetry}
            className="flex items-center gap-2 px-6 py-3 bg-cyan-600 text-white rounded-xl font-medium hover:bg-cyan-700 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Try Again
          </button>
          <p className="text-sm text-slate-500">
            Or go back to upload a different document
          </p>
        </div>

        {/* Troubleshooting tips */}
        <div className="mt-8 p-4 bg-slate-50 rounded-lg">
          <p className="text-sm font-medium text-slate-700 mb-2">Troubleshooting:</p>
          <ul className="text-sm text-slate-600 space-y-1">
            <li>• Ensure the document contains visible checklist items</li>
            <li>• Try a clearer scan or higher resolution image</li>
            <li>• Check that the file isn't corrupted or password-protected</li>
            <li>• Try uploading a different page of the document</li>
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="text-center mb-8">
        <div className="relative w-20 h-20 mx-auto mb-4">
          <div className="absolute inset-0 bg-cyan-100 rounded-full animate-ping opacity-25" />
          <div className="relative w-20 h-20 bg-cyan-100 rounded-full flex items-center justify-center">
            <Sparkles className="h-10 w-10 text-cyan-600" />
          </div>
        </div>
        <h3 className="text-lg font-semibold text-slate-800">Analyzing Document</h3>
        <p className="text-sm text-slate-500 mt-1">
          AI is extracting checklist items from your document
        </p>
      </div>

      {/* File being processed */}
      <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg mb-6">
        <FileText className="h-5 w-5 text-slate-400" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-700 truncate">{file?.name}</p>
          <p className="text-xs text-slate-500">
            {file ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : ''}
          </p>
        </div>
        <Loader2 className="h-5 w-5 text-cyan-600 animate-spin" />
      </div>

      {/* Processing stages */}
      <div className="space-y-3">
        {stages.map((stage, index) => {
          const isActive = isProcessing && stages.findIndex(s => !s.complete) === index;
          const isPending = isProcessing && index > stages.findIndex(s => !s.complete);

          return (
            <div key={stage.id} className="flex items-center gap-3">
              {stage.complete ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : isActive ? (
                <Loader2 className="h-5 w-5 text-cyan-600 animate-spin" />
              ) : (
                <div className="w-5 h-5 rounded-full border-2 border-slate-300" />
              )}
              <span className={`text-sm ${stage.complete ? 'text-green-700' : isActive ? 'text-cyan-700 font-medium' : 'text-slate-400'}`}>
                {stage.label}
              </span>
            </div>
          );
        })}
      </div>

      {/* Processing info */}
      <div className="mt-8 p-4 bg-cyan-50 border border-cyan-200 rounded-lg">
        <p className="text-sm text-cyan-800">
          <strong>What's happening:</strong> Claude AI is reading your document using vision
          technology to identify and extract checklist items. This typically takes 10-30 seconds.
        </p>
      </div>
    </div>
  );
};

export default ProcessingStep;
